import React, { useCallback, useMemo, useState } from 'react';
import CircleIcon from '@mui/icons-material/Circle';
import {
  VictoryChart,
  VictoryStack,
  VictoryBar,
  VictoryAxis,
  VictoryTooltip
} from 'victory';
import makeStyles from '@mui/styles/makeStyles';
import { useTheme } from '@mui/styles';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import { useIntl, FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { SelectChangeEvent } from '@mui/material/Select';
import { getFormattedDuration } from 'modules/notes/common/components/Timer/useFormattedDuration';
import { useScrollbarStyles } from 'modules/common/styles/useScrollbarStyles';
import { useGetTimeSignalAnalyticsData } from 'modules/Insights/Notes/hooks/useGetTimeSignalAnalyticsData';
import { TimeSignalDimensionType } from 'graphql/types';
import { transformAnalyticsData } from './util';

import TimeFormatComponent from './TimeFormatComponent';
import SourceAvatar from './SourceAvatar';
import { useChartStyles } from './hooks/useChartStyles';
import SourceByTimeChartSkeleton from './SourceByTimeChartSkeleton';
import SourceByTimeChartNoDataFound from './SourceByTimeChartNoData';
import SourceDropDown from './SourceDropDown';

const useStyles = makeStyles((theme) => ({
  tooltip: {
    marginTop: `${theme.spacing(-2)} !important`
  },
  container: {
    width: '100%',
    padding: `${theme.spacing(2)} ${theme.spacing(2.5)}`
  },
  heading: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    fontSize: theme.spacing(2.6)
  },
  legendText: {
    paddingLeft: ({
      isMetricsByCollectors
    }: {
      isMetricsByCollectors: boolean;
    }) => (!isMetricsByCollectors ? theme.spacing(2) : undefined),
    color: theme.palette.text.secondary
  },
  chartContainer: {
    paddingTop: theme.spacing(1)
  },
  legendContainer: {
    marginTop: theme.spacing(3),
    height: '50vh',
    maxHeight: '50vh',
    overflow: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    width: '100%'
  },
  legendRows: {
    paddingTop: theme.spacing(1.5),
    paddingBottom: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5)
  },
  bulletIcon: {
    width: theme.spacing(1.5),
    height: theme.spacing(1.5)
  }
}));

const MyCustomFlyout: React.FC<any> = ({
  x,
  y,
  value
}: {
  x: number;
  y: number;
  value: string;
}) => (
  <foreignObject x={x - 40} y={y - 55} width="90" height="50">
    <Tooltip title={value} open={true}>
      <span />
    </Tooltip>
  </foreignObject>
);

const SourceByTimeChartFC: React.FC = () => {
  const classesScrollbar = useScrollbarStyles();
  const theme = useTheme();

  const style = useChartStyles(theme);

  const { formatMessage } = useIntl();

  const selectDefaultText = formatMessage({
    id: 'insights.timeBreakdownChart.collectors'
  });
  const [selectvalue, setSelectValue] = useState(selectDefaultText);

  const onSelectValueChange = useCallback((event: SelectChangeEvent) => {
    setSelectValue(event.target.value);
  }, []);

  const isMetricsByCollectors = useMemo(() => {
    return selectvalue === selectDefaultText;
  }, [selectDefaultText, selectvalue]);

  const classes = useStyles({ isMetricsByCollectors });
  const { data, loading, isEmpty } = useGetTimeSignalAnalyticsData(
    isMetricsByCollectors
      ? TimeSignalDimensionType.MetricsBySource
      : TimeSignalDimensionType.MetricsByCategory
  );
  const dataset = useMemo(() => {
    if (isEmpty) {
      return [];
    } else return transformAnalyticsData(data?.timeSignalAnalytics?.items);
  }, [data?.timeSignalAnalytics?.items, isEmpty]);

  return (
    <Paper
      className={classes.container}
      elevation={3}
      data-testid={'source-by-time-chart-container'}
    >
      <Grid container>
        <Grid container item>
          <Grid
            item
            xs={12}
            container
            alignItems={'center'}
            justifyContent="space-between"
          >
            <Typography variant="h4" className={classes.heading}>
              <FormattedMessage id="insights.timeBreakdownChart.title" />
            </Typography>
            <SourceDropDown
              value={selectvalue}
              onChange={onSelectValueChange}
              disabled={(isEmpty && isMetricsByCollectors) || loading}
            />
          </Grid>
        </Grid>
        {loading ? (
          <SourceByTimeChartSkeleton />
        ) : (
          <>
            {!isEmpty && (
              <>
                <Grid
                  container
                  item
                  className={classes.chartContainer}
                  data-testid={'source-by-time-chart'}
                >
                  <VictoryChart
                    height={style.VictoryChart.height}
                    domainPadding={style.VictoryChart.domainPadding}
                    padding={style.VictoryChart.padding}
                    animate={{
                      duration: 1000,
                      easing: 'bounce'
                    }}
                  >
                    <VictoryStack>
                      {dataset.map((data, i) => {
                        const formattedTime = getFormattedDuration({
                          formatMessage,
                          duration: data[0].value ?? 0,
                          needsMask: true
                        });
                        return (
                          <VictoryBar
                            labels={() => ''}
                            labelComponent={
                              <VictoryTooltip
                                flyoutPadding={10}
                                flyoutComponent={
                                  <MyCustomFlyout
                                    value={`${data[0].name}: ${formattedTime}`}
                                  />
                                }
                              />
                            }
                            horizontal
                            data={data}
                            key={i}
                            barWidth={style.VictoryBar.width}
                            style={{
                              data: { fill: data[0].color }
                            }}
                            events={[
                              {
                                target: 'data',
                                eventHandlers: {
                                  onMouseOver: () => {
                                    return [
                                      {
                                        target: 'data',
                                        mutation: (props) => {
                                          return {
                                            style: {
                                              // eslint-disable-next-line react/prop-types
                                              fill: props.datum.color,
                                              stroke:
                                                theme.palette.information
                                                  .background,
                                              strokeWidth: 1,
                                              cursor: 'pointer'
                                            }
                                          };
                                        }
                                      },
                                      {
                                        target: 'labels',
                                        mutation: () => ({ active: true })
                                      }
                                    ];
                                  },
                                  onMouseLeave: () => {
                                    return [
                                      {
                                        target: 'data',
                                        mutation: (props) => {
                                          return {
                                            style: {
                                              // eslint-disable-next-line react/prop-types
                                              fill: props.datum.color
                                            }
                                          };
                                        }
                                      },
                                      {
                                        target: 'labels',
                                        mutation: () => ({ active: undefined })
                                      }
                                    ];
                                  }
                                }
                              }
                            ]}
                          />
                        );
                      })}
                    </VictoryStack>
                    <VictoryAxis style={style.VictoryAxis} />
                  </VictoryChart>
                </Grid>
                <div
                  className={classnames(
                    classes.legendContainer,
                    classesScrollbar.scrollbar
                  )}
                >
                  {dataset.map((data) => (
                    <Grid
                      container
                      key={JSON.stringify(data)}
                      className={classes.legendRows}
                    >
                      <Grid
                        item
                        xs={8}
                        container
                        alignItems={'center'}
                        data-testid={`legend-text-${data[0].name}`}
                      >
                        <CircleIcon
                          fontSize="small"
                          sx={{ color: data[0].color }}
                          className={classes.bulletIcon}
                        />
                        {isMetricsByCollectors && (
                          <SourceAvatar
                            name={data[0].name}
                            src={data[0].imgSrc}
                            data-testid={'source-avatar'}
                          />
                        )}
                        <Grid item xs={8}>
                          <Tooltip
                            title={data[0].name}
                            classes={{ popper: classes.tooltip }}
                          >
                            <Typography
                              variant="body1"
                              className={classes.legendText}
                              noWrap={true}
                            >
                              {data[0].name}
                            </Typography>
                          </Tooltip>
                        </Grid>
                      </Grid>
                      <Grid
                        container
                        item
                        xs={4}
                        justifyContent={'flex-end'}
                        alignItems={'center'}
                      >
                        <TimeFormatComponent duration={data[0].value} />
                      </Grid>
                    </Grid>
                  ))}
                </div>
              </>
            )}
            {isEmpty && <SourceByTimeChartNoDataFound />}
          </>
        )}
      </Grid>
    </Paper>
  );
};

export default React.memo(SourceByTimeChartFC);
